/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons/faAngleDown'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { router } from 'expo-router'
import { useState } from 'react'
import Select, { components, createFilter } from 'react-select'
import { Button, colorWithOpacity, Spacing, Text, useTheme } from 'ui'
import { useStore } from '@/providers/store'
import { fieldErrors } from '@/queries/query-client'
import { useProxyUser, useUser, useUsers } from '@/queries/user.queries'
import { User, userRoleSelect } from '@/types/user'

export function ProxyUser() {
  const theme = useTheme()

  const user = useUser()
  const users = useUsers()
  const proxyUser = useProxyUser()

  const [selectedUser, setSelectedUser] = useState<User>()
  const [error, setError] = useState<{ user?: string }>()

  const styles = {
    wrapper: css({
      padding: `0 ${theme.spacing * 1.4}px`,
    }),
  }

  return (
    <div css={styles.wrapper}>
      <Spacing height={1.2} />
      <UserSelect
        label='User'
        value={selectedUser}
        options={users.data?.filter((u) => u.id !== user.data?.id && u.active && !(user.data?.role === 'executive' && u.role === 'finance')) ?? []}
        placeholder='Select user'
        noOptions='No user found'
        error={error?.user}
        onChange={(user) => setSelectedUser(user)}
      />
      <Spacing height={1.4} />
      <Button
        text='Proxy User'
        type='primary'
        loading={proxyUser.isPending}
        disabled={!users.data}
        onPress={() => {
          proxyUser.mutate({ id: selectedUser?.id ?? 0 }, {
            onSuccess: () => {
              useStore.getState().modal.close()
              router.navigate('/')
            },
            onError: (error) => setError(fieldErrors(error)),
          })
        }}
      />
      <Spacing height={1.4} />
    </div>
  )
}

function UserSelect({
  label,
  value,
  options,
  placeholder,
  noOptions,
  compact,
  disabled,
  error,
  onChange,
}: {
  label: string
  value: User | undefined
  options: User[]
  placeholder: string
  noOptions: string
  compact?: boolean
  disabled?: boolean
  error?: string
  onChange: (user: User) => void
}) {
  const theme = useTheme()

  const height = compact ? theme.input.compact.height : theme.input.height

  return (
    <div>
      {label && <Text type='inputLabel' compact={compact}>{label}</Text>}

      <Select
        value={value}
        placeholder={placeholder}
        options={options}
        noOptionsMessage={({ inputValue }) => inputValue.length ? noOptions : null}
        isDisabled={disabled}
        filterOption={createFilter({ stringify: (user) => user.data.fullName ?? '' })}
        getOptionValue={(user) => user.id.toString()}
        getOptionLabel={(user) => `${user.fullName} - ${user.email}`}
        maxMenuHeight={350}
        components={{
          DropdownIndicator: (props) => (
            <FontAwesomeIcon
              icon={faAngleDown}
              size={compact ? 17 : 19}
              color={colorWithOpacity(theme.colors.foreground, props.isDisabled ? 0.35 : 0.7)}
              style={{ marginRight: 12 }}
            />
          ),
          Option: (props) => (
            <components.Option {...props}>
              <div className='name'>{props.data.fullName}</div>
              <div className='meta'>{props.data.email}</div>
              <div className='meta'>{userRoleSelect(props.data)}</div>
            </components.Option>
          ),
        }}
        styles={{
          container: (base) => ({
            ...base,
            fontFamily: theme.input.fontFamily,
            fontSize: compact ? theme.input.compact.fontSize : theme.input.fontSize,
          }),
          control: (base, props) => ({
            ...base,
            minHeight: height,
            background: theme.colors.surface,
            borderColor: error ? theme.colors.danger : props.isFocused ? theme.colors.primary : theme.input.border,
            borderRadius: theme.borderRadius.s,
            boxShadow: theme.web.shadow.input,
            outline: 'none',
            transition: 'none',
            cursor: 'pointer',
            color: theme.colors.foreground,
            opacity: props.isDisabled ? 0.5 : 1,
            ':hover': {
              borderColor: error ? theme.colors.danger : props.isFocused ? theme.colors.primary : theme.input.borderHover,
            },
          }),
          valueContainer: (base) => ({
            ...base,
            padding: '0 12px',
          }),
          singleValue: (base) => ({
            ...base,
            color: theme.colors.foreground,
          }),
          input: (base) => ({
            ...base,
            color: theme.colors.foreground,
          }),
          placeholder: (base) => ({
            ...base,
            color: theme.input.placeholder,
          }),
          indicatorSeparator: () => ({
            display: 'none',
          }),
          menu: (base) => ({
            ...base,
            borderRadius: theme.borderRadius.s,
            boxShadow: theme.web.shadow.overlay,
          }),
          menuList: (base) => ({
            ...base,
            padding: 4,
          }),
          option: (base, props) => ({
            ...base,
            fontSize: 15,
            lineHeight: 1.4,
            padding: '8px 10px',
            borderRadius: theme.borderRadius.s - 2,
            background: props.isSelected ? theme.colors.primary
              : props.isFocused ? colorWithOpacity(theme.colors.foreground, 0.07) : undefined,
            color: props.isSelected ? '#FFFFFF' : theme.colors.foreground,
            cursor: 'pointer',
            ':active': {
              background: colorWithOpacity(theme.colors.foreground, 0.1),
            },
            '> .name': {
              fontFamily: theme.fonts.body[500],
            },
            '> .meta': {
              fontSize: 14.5,
              opacity: 0.8,
              marginTop: 1,
            },
          }),
          noOptionsMessage: (base) => ({
            ...base,
            color: colorWithOpacity(theme.colors.foreground, 0.8),
            fontSize: 15,
            textAlign: 'left',
          })
        }}
        onChange={(user) => user ? onChange(user) : undefined}
      />

      {error && <Text type='inputError' compact={compact}>{error}</Text>}
    </div>
  )
}
